'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { HomepageFlashSection, defaultHomepageConfig } from '@/lib/homepage-config';
import ProductSpecsHoverCard from '@/components/ProductSpecsHoverCard';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { buildProductCardSpecBadges } from '@/lib/product-spec-badges';
import AppImage from '@/components/ui/AppImage';

type FlashProduct = {
  id: string;
  name: string;
  slug: string;
  image: string;
  price: number;
  original_price: number;
  discount: number;
  specs?: string;
  full_specs?: Record<string, string>;
  /** Optional để card dùng ở nơi chưa truyền tồn kho; mặc định coi như hết hàng. */
  stock?: number;
};

function discountPercent(product: FlashProduct): number {
  const p = Number(product.price || 0);
  const o = Number(product.original_price || 0);
  if (product.discount > 0) return Math.min(99, Math.round(product.discount));
  if (o > p && o > 0) return Math.min(99, Math.round((1 - p / o) * 100));
  return 0;
}

const tabPillActive = [
  'bg-blue-600 text-white shadow-md shadow-blue-600/25',
  'bg-emerald-600 text-white shadow-md shadow-emerald-600/25',
  'bg-amber-600 text-white shadow-md shadow-amber-600/25',
];

function normalizeFlashText(value: string) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function buildFlashTabKey(sectionTitle: string, tabLabel: string) {
  return `${normalizeFlashText(sectionTitle)}::${normalizeFlashText(tabLabel)}`;
}

function SpecIcon({ index }: { index: number }) {
  const paths = [
    'M8 1.5 2.5 4.5v7L8 14.5l5.5-3v-7L8 1.5Zm0 2.2 3.5 1.9L8 7.5 4.5 5.6 8 3.7Zm-3.8 3 3 1.6v3.4l-3-1.6V6.7Zm7.6 0v3.4l-3 1.6V8.3l3-1.6Z',
    'M3 2h10a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9.5L8 14l-1.5-2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm1 3v1h8V5H4Zm0 3v1h5V8H4Z',
    'M2 3h12v8H9.5v2H12v1H4v-1h2.5v-2H2V3Zm1.5 1.5v5h9v-5h-9Z',
    'M4 2h8v12H4V2Zm1.5 1.5v9h5v-9h-5ZM7 11h2v1H7v-1Z',
    'M2 4.5A2.5 2.5 0 0 1 4.5 2h7A2.5 2.5 0 0 1 14 4.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 11.5v-7Zm2.5-1A1 1 0 0 0 3.5 4.5v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-7ZM5 5h6v1H5V5Zm0 2.5h6v1H5v-1Zm0 2.5h4v1H5v-1Z',
    'M8 1.5A6.5 6.5 0 1 1 8 14.5 6.5 6.5 0 0 1 8 1.5Zm0 2A4.5 4.5 0 1 0 8 12.5 4.5 4.5 0 0 0 8 3.5Zm-.5 1.5h1v3.2l2.2 1.3-.5.9-2.7-1.6V5Z',
  ];

  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5 shrink-0 text-slate-500"
      fill="currentColor"
      aria-hidden
    >
      <path d={paths[index % paths.length]} />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 4.5h2.2l2.1 10.2a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 1.9-1.4l1.3-5.2H7" />
      <path d="M8.4 9.7h11.7" />
      <path d="M9.3 13h9.9" />
      <path d="M11.5 9.7v6.6" />
      <path d="M16.1 9.7v6.6" />
      <circle cx="10.2" cy="20" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

function getFlashSectionCategoryHref(section: HomepageFlashSection) {
  const title = normalizeFlashText(section.title).replace(/đ/g, 'd');
  const firstTabHref = section.tabs.find((tab) => tab.href && tab.href !== '#')?.href;

  if (title.includes('mainboard') || title.includes('bo mach')) return '/danh-muc/mainboard';
  if (title.includes('cpu') || title.includes('vi xu ly')) return '/danh-muc/cpu';
  if (title.includes('vga') || title.includes('card man hinh')) return '/danh-muc/vga';
  if (title.includes('ram')) return '/danh-muc/ram';
  if (title.includes('ssd')) return '/danh-muc/ssd-nvme';
  if (title.includes('hdd') || title.includes('o cung')) return '/danh-muc/hdd';
  if (title.includes('man hinh') || title.includes('monitor')) return '/danh-muc/man-hinh';
  if (title.includes('laptop')) return '/danh-muc/laptop';
  if (title.includes('case') || title.includes('vo case') || title.includes('thung may')) {
    return '/danh-muc/case-gaming';
  }
  if (title.includes('tan') || title.includes('cooling') || title.includes('cooler')) {
    return '/danh-muc/tan-nhiet';
  }
  if (title.includes('nguon') || title.includes('psu')) return '/danh-muc/nguon-may-tinh';
  if (title.includes('phim') || title.includes('ban phim') || title.includes('keyboard')) {
    return '/danh-muc/ban-phim';
  }
  if (title.includes('chuot') || title.includes('mouse')) return '/danh-muc/chuot';
  if (title.includes('tai nghe') || title.includes('headset')) return '/danh-muc/tai-nghe';
  if (title.includes('thiet bi mang') || title.includes('router') || title.includes('switch')) {
    return '/danh-muc/thiet-bi-mang';
  }
  if (title.includes('may in') || title.includes('printer')) return '/danh-muc/may-in-mau';
  if (title.includes('ups') || title.includes('bo luu dien')) return '/danh-muc/ups';
  if (title.includes('camera')) return '/danh-muc/camera-trong-nha';
  if (title.includes('webcam')) return '/danh-muc/webcam';

  if (section.viewAllHref?.startsWith('/tim-kiem')) return firstTabHref || '#';
  return section.viewAllHref || firstTabHref || '#';
}

const FlashProductCard = React.memo(
  ({
    product,
    idx,
    handleAddToCart,
  }: {
    product: FlashProduct;
    idx: number;
    handleAddToCart: (
      e: React.MouseEvent<HTMLButtonElement>,
      product: FlashProduct,
      href: string
    ) => void;
  }) => {
    const pct = discountPercent(product);
    const showDeal = pct > 0;
    const specsList = buildProductCardSpecBadges(product.specs, product.full_specs, product.name);

    const productHref = `/san-pham/${product.slug}`;
    const [imgSrc, setImgSrc] = useState(product.image);
    const stock = Math.max(0, Math.floor(Number(product.stock ?? 0)));
    const isOutOfStock = stock <= 0;

    return (
      <div
        data-flash-card="1"
        className="group relative flex min-h-[552px] min-w-[176px] w-[calc((100%-0.75rem)/2)] shrink-0 snap-start flex-col rounded-none border border-slate-200 bg-white p-3 transition-colors duration-200 hover:border-slate-300 sm:w-[calc((100%-1.5rem)/3)] md:w-[calc((100%-2.25rem)/4)] lg:w-[calc((100%-4rem)/5)]"
      >
        <button
          type="button"
          onClick={(event) => handleAddToCart(event, product, productHref)}
          disabled={isOutOfStock}
          className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center bg-transparent p-0 text-black transition hover:scale-105 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
          aria-label={isOutOfStock ? 'Sản phẩm đã hết hàng' : 'Thêm sản phẩm vào giỏ hàng'}
          title={isOutOfStock ? 'Hết hàng' : 'Thêm vào giỏ hàng'}
        >
          <CartIcon />
        </button>
        <Link href={productHref} className="flex flex-col flex-1" draggable={false}>
          <div className="group/spec-trigger relative mb-3 aspect-[1.18/1] overflow-visible bg-white pt-5">
            <AppImage
              src={imgSrc}
              alt={product.name}
              fill
              sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"
              className="pointer-events-none select-none object-contain p-3 mix-blend-multiply"
              onError={() => setImgSrc('/assets/images/no_image.png')}
            />
            <ProductSpecsHoverCard
              name={product.name}
              price={Number(product.price || 0)}
              originalPrice={Number(product.original_price || 0)}
              stock={stock}
              fullSpecs={product.full_specs || {}}
              specsText={product.specs || ''}
            />
          </div>
          <h3 className="mb-2 min-h-[7.1rem] break-words text-left text-[14px] font-bold leading-[1.45] text-slate-800">
            {product.name}
          </h3>
          <div className="mb-3 bg-[#ececec] px-2.5 py-2 text-[12px] text-slate-600">
            {specsList.length ? (
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                {specsList.map((spec, specIndex) => (
                  <div key={`${spec}-${specIndex}`} className="flex min-w-0 items-center gap-1.5">
                    <SpecIcon index={specIndex} />
                    <span className="truncate leading-snug">{spec}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="line-clamp-1">Thông số đang cập nhật</div>
            )}
          </div>
          <div className="mt-auto text-left">
            {Number(product.original_price || 0) > Number(product.price || 0) ? (
              <div className="mb-0.5 text-[13px] text-slate-500 line-through">
                {new Intl.NumberFormat('vi-VN').format(Number(product.original_price || 0))}₫
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[18px] font-black leading-none text-red-600">
                {new Intl.NumberFormat('vi-VN').format(Number(product.price || 0))}₫
              </span>
              {showDeal ? (
                <span className="border border-red-500 px-1.5 py-0.5 text-[12px] font-medium leading-none text-red-600">
                  -{pct}%
                </span>
              ) : null}
            </div>
            <div className="mt-2 text-[13px] font-semibold text-orange-500">
              0.0 ★ <span className="font-normal text-slate-500">(0 đánh giá)</span>
            </div>
          </div>
        </Link>
      </div>
    );
  }
);

FlashProductCard.displayName = 'FlashProductCard';

export default function FlashSaleSections({
  sections = defaultHomepageConfig.flashSections,
  productsByTab = {},
}: {
  sections?: HomepageFlashSection[];
  productsByTab?: Record<string, FlashProduct[]>;
}) {
  const router = useRouter();
  const { addToCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [activeTabs, setActiveTabs] = useState<Record<number, number>>({});
  const [cartMessage, setCartMessage] = useState('');
  const [draggingSection, setDraggingSection] = useState<number | null>(null);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const scrollRefs = useRef(new Map<number, HTMLDivElement | null>());
  const momentumFrameRefs = useRef(new Map<number, number>());
  const dragStateRef = useRef<{
    sectionIndex: number | null;
    pointerId: number | null;
    startX: number;
    startScrollLeft: number;
    lastX: number;
    lastTime: number;
    velocityX: number;
    moved: boolean;
    wrapToStart: boolean;
    suppressClickUntil: number;
  }>({
    sectionIndex: null,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocityX: 0,
    moved: false,
    wrapToStart: false,
    suppressClickUntil: 0,
  });

  const safeSections = useMemo(() => {
    const items = sections
      .filter((section) => section.enabled)
      .sort((a, b) => a.sort_order - b.sort_order);
    return items.length ? items : defaultHomepageConfig.flashSections;
  }, [sections]);

  const getActiveTab = (sectionIndex: number) => activeTabs[sectionIndex] ?? 0;
  const getProducts = (section: HomepageFlashSection, sectionIndex: number, tabIndex: number) => {
    const tab = section.tabs[tabIndex];
    const stableKey = tab ? buildFlashTabKey(section.title, tab.label) : '';
    if (stableKey && productsByTab[stableKey]) return productsByTab[stableKey];
    return productsByTab[`${sectionIndex}:${tabIndex}`] || [];
  };

  const getScrollMetrics = useCallback((sectionIndex: number) => {
    const el = scrollRefs.current.get(sectionIndex);
    if (!el) return null;

    const firstCard = el.querySelector<HTMLElement>('[data-flash-card="1"]');
    const track = el.firstElementChild as HTMLElement | null;
    const gap = track
      ? parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '12')
      : 12;
    const cardWidth = firstCard?.offsetWidth || 188;
    const itemStep = cardWidth + gap;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);

    return { el, itemStep, maxLeft };
  }, []);

  const snapToNearest = useCallback(
    (sectionIndex: number) => {
      const metrics = getScrollMetrics(sectionIndex);
      if (!metrics) return;

      const { el, itemStep, maxLeft } = metrics;
      const currentScroll = el.scrollLeft;
      const nearestItemIndex = Math.round(currentScroll / itemStep);
      const target = Math.min(maxLeft, Math.max(0, nearestItemIndex * itemStep));

      if (Math.abs(el.scrollLeft - target) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
      }
    },
    [getScrollMetrics]
  );

  const scrollSection = useCallback(
    (sectionIndex: number, dir: -1 | 1) => {
      const metrics = getScrollMetrics(sectionIndex);
      if (!metrics) return;

      const { el, itemStep, maxLeft } = metrics;
      const itemsPerView = Math.max(1, Math.floor(el.clientWidth / itemStep));
      const step = itemStep * Math.max(1, itemsPerView - 1);
      const nextLeft = dir > 0 ? el.scrollLeft + step : el.scrollLeft - step;
      const target = Math.min(maxLeft, Math.max(0, nextLeft));
      el.scrollTo({ left: target, behavior: 'smooth' });
    },
    [getScrollMetrics]
  );

  const autoAdvanceSection = useCallback(
    (sectionIndex: number) => {
      const metrics = getScrollMetrics(sectionIndex);
      if (!metrics) return;

      const { el, itemStep, maxLeft } = metrics;
      if (maxLeft <= 0) return;

      const nextLeft = el.scrollLeft + itemStep;
      const nearEnd = el.scrollLeft >= maxLeft - Math.max(20, itemStep * 0.35);
      const target = nearEnd ? 0 : Math.min(maxLeft, nextLeft);
      el.scrollTo({ left: target, behavior: 'smooth' });
    },
    [getScrollMetrics]
  );

  const setScrollRef = (sectionIndex: number, el: HTMLDivElement | null) => {
    if (el) scrollRefs.current.set(sectionIndex, el);
    else scrollRefs.current.delete(sectionIndex);
  };

  const stopMomentum = useCallback((sectionIndex: number) => {
    const frame = momentumFrameRefs.current.get(sectionIndex);
    if (frame) {
      window.cancelAnimationFrame(frame);
      momentumFrameRefs.current.delete(sectionIndex);
    }
  }, []);

  const startMomentum = useCallback(
    (sectionIndex: number, initialVelocityX: number) => {
      const el = scrollRefs.current.get(sectionIndex);
      if (!el) {
        setDraggingSection((current) => (current === sectionIndex ? null : current));
        return;
      }

      stopMomentum(sectionIndex);

      let velocity = initialVelocityX;
      let lastTime = performance.now();

      const step = (now: number) => {
        const node = scrollRefs.current.get(sectionIndex);
        if (!node) {
          momentumFrameRefs.current.delete(sectionIndex);
          setDraggingSection((current) => (current === sectionIndex ? null : current));
          return;
        }

        const dt = Math.min(32, now - lastTime || 16);
        lastTime = now;

        node.scrollLeft -= velocity * dt;
        velocity *= Math.pow(0.91, dt / 16);

        const reachedEdge =
          node.scrollLeft <= 0 || node.scrollLeft >= node.scrollWidth - node.clientWidth - 1;

        if (Math.abs(velocity) < 0.12 || reachedEdge) {
          momentumFrameRefs.current.delete(sectionIndex);
          setDraggingSection((current) => (current === sectionIndex ? null : current));

          if (!reachedEdge) {
            snapToNearest(sectionIndex);
          }
          return;
        }

        const nextFrame = window.requestAnimationFrame(step);
        momentumFrameRefs.current.set(sectionIndex, nextFrame);
      };

      const frame = window.requestAnimationFrame(step);
      momentumFrameRefs.current.set(sectionIndex, frame);
    },
    [stopMomentum, snapToNearest]
  );

  const handleTrackPointerDown = useCallback(
    (sectionIndex: number, event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;

      const el = scrollRefs.current.get(sectionIndex);
      if (!el) return;

      stopMomentum(sectionIndex);

      dragStateRef.current = {
        sectionIndex,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: el.scrollLeft,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocityX: 0,
        moved: false,
        wrapToStart: false,
        suppressClickUntil: 0,
      };

      setDraggingSection(sectionIndex);
    },
    [stopMomentum]
  );

  const handleTrackPointerMove = useCallback(
    (sectionIndex: number, event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'mouse') return;

      const state = dragStateRef.current;
      if (state.sectionIndex !== sectionIndex || state.pointerId !== event.pointerId) return;

      const el = scrollRefs.current.get(sectionIndex);
      if (!el) return;

      const deltaX = event.clientX - state.startX;
      if (!state.moved && Math.abs(deltaX) > 4) {
        state.moved = true;
        if (!el.hasPointerCapture(event.pointerId)) {
          el.setPointerCapture(event.pointerId);
        }
      }

      if (!state.moved) return;

      event.preventDefault();
      const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const rawScrollLeft = state.startScrollLeft - deltaX;

      const finalScrollLeft = Math.max(0, Math.min(maxLeft, rawScrollLeft));
      el.scrollLeft = finalScrollLeft;

      state.wrapToStart = maxLeft > 0 && rawScrollLeft - maxLeft > 56;

      const now = performance.now();
      const deltaTime = Math.max(1, now - state.lastTime);
      const deltaMove = event.clientX - state.lastX;
      const currentVelocity = deltaMove / deltaTime;
      state.velocityX = state.velocityX * 0.35 + currentVelocity * 0.65;
      state.lastX = event.clientX;
      state.lastTime = now;
    },
    []
  );

  const finishTrackDrag = useCallback(
    (sectionIndex: number, event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType !== 'mouse') return;

      const state = dragStateRef.current;
      if (state.sectionIndex !== sectionIndex || state.pointerId !== event.pointerId) return;

      const el = scrollRefs.current.get(sectionIndex);
      if (el?.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }

      const shouldSuppressClick = state.moved;
      const releaseVelocityX = state.velocityX;
      const shouldWrapToStart = state.wrapToStart;

      dragStateRef.current = {
        sectionIndex: null,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        lastX: 0,
        lastTime: 0,
        velocityX: 0,
        moved: false,
        wrapToStart: false,
        suppressClickUntil: shouldSuppressClick ? Date.now() + 250 : 0,
      };

      if (shouldWrapToStart) {
        el?.scrollTo({ left: 0, behavior: 'smooth' });
        setDraggingSection((current) => (current === sectionIndex ? null : current));
        return;
      }

      if (shouldSuppressClick && Math.abs(releaseVelocityX) > 0.08) {
        startMomentum(sectionIndex, releaseVelocityX);
        return;
      }

      setDraggingSection((current) => (current === sectionIndex ? null : current));

      if (shouldSuppressClick) {
        snapToNearest(sectionIndex);
      }
    },
    [startMomentum, snapToNearest]
  );

  const handleTrackClickCapture = useCallback(
    (sectionIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (state.sectionIndex !== null || draggingSection !== null) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (state.suppressClickUntil > Date.now()) {
        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current.suppressClickUntil = 0;
        return;
      }

      if (state.suppressClickUntil <= Date.now()) {
        dragStateRef.current.suppressClickUntil = 0;
      }
    },
    [draggingSection]
  );

  const handleAddToCart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, product: FlashProduct, redirectHref: string) => {
      event.preventDefault();
      event.stopPropagation();

      if (authLoading) return;

      const stock = Math.max(0, Math.floor(Number(product.stock ?? 0)));
      if (stock <= 0) {
        setCartMessage('Sản phẩm hiện đang hết hàng.');
        setTimeout(() => setCartMessage(''), 2500);
        return;
      }

      if (!user) {
        setCartMessage('Vui lòng đăng nhập để thêm sản phẩm vào giỏ hàng.');
        setTimeout(() => {
          router.push(`/dang-nhap?redirect=${encodeURIComponent(redirectHref)}`);
        }, 300);
        return;
      }

      const added = addToCart({
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: Number(product.price || 0),
        original_price: Number(product.original_price || 0),
        image: product.image,
        stock,
      });

      if (!added) {
        setCartMessage('Không thể thêm sản phẩm, số lượng đã đạt mức tồn kho.');
        setTimeout(() => setCartMessage(''), 2500);
        return;
      }

      setCartMessage('Đã thêm sản phẩm vào giỏ hàng.');
      setTimeout(() => setCartMessage(''), 2500);
    },
    [addToCart, authLoading, router, user]
  );

  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setAutoPlayEnabled(!media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const index = Number((entry.target as HTMLElement).dataset.sectionIndex);
            if (entry.isIntersecting) next.add(index);
            else next.delete(index);
          });
          if (next.size === prev.size && [...next].every((item) => prev.has(item))) return prev;
          return next;
        });
      },
      { threshold: 0.1 }
    );

    const currentRefs = scrollRefs.current;
    currentRefs.forEach((el, index) => {
      if (el?.parentElement?.parentElement) {
        (el.parentElement.parentElement as HTMLElement).dataset.sectionIndex = String(index);
        observer.observe(el.parentElement.parentElement);
      }
    });

    return () => observer.disconnect();
  }, [safeSections]);

  useEffect(() => {
    if (!autoPlayEnabled || !visibleSections.size) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;

      const sectionIndex = [...visibleSections].sort((a, b) => a - b)[0];
      const section = safeSections[sectionIndex];
      if (!section) return;
      if (draggingSection === sectionIndex) return;
      if (momentumFrameRefs.current.has(sectionIndex)) return;

      const activeTab = getActiveTab(sectionIndex);
      const sectionProducts = getProducts(section, sectionIndex, activeTab);
      if (sectionProducts.length <= 1) return;

      autoAdvanceSection(sectionIndex);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [
    activeTabs,
    autoAdvanceSection,
    autoPlayEnabled,
    draggingSection,
    safeSections,
    productsByTab,
    visibleSections,
  ]);

  return (
    <div className="mx-auto w-full max-w-[1500px] px-3 sm:px-4 space-y-5">
      {cartMessage ? (
        <div className="sticky top-[calc(var(--site-header-height,92px)+10px)] z-20 -mb-2">
          <div className="mx-auto max-w-fit rounded-none border border-amber-200 bg-white/95 px-4 py-2 text-sm font-semibold text-amber-800 shadow-sm">
            {cartMessage}
          </div>
        </div>
      ) : null}
      {safeSections.map((section, sectionIndex) => {
        const activeTab = getActiveTab(sectionIndex);
        const products = getProducts(section, sectionIndex, activeTab);
        const viewAllHref = getFlashSectionCategoryHref(section);

        return (
          <section
            key={`${section.title}-${sectionIndex}`}
            className="overflow-hidden rounded-none border border-slate-200/80 bg-white shadow-sm [content-visibility:auto] [contain-intrinsic-size:620px]"
          >
            <div className="flex flex-col gap-3 border-b border-slate-100/90 bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5 sm:gap-3">
                <span
                  className="hidden h-8 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-400 to-orange-500 sm:block"
                  aria-hidden
                />
                <h2 className="text-lg font-black uppercase tracking-tight text-slate-900 sm:text-xl">
                  {section.title}
                </h2>
                <div className="flex flex-wrap items-center gap-1.5">
                  {section.tabs.map((tab, tabIndex) => (
                    <button
                      key={`${tab.label}-${tabIndex}`}
                      type="button"
                      onClick={() =>
                        setActiveTabs((prev) => ({ ...prev, [sectionIndex]: tabIndex }))
                      }
                      aria-pressed={activeTab === tabIndex}
                      className={`flash-section-tab px-3 py-1 text-xs font-semibold transition-colors duration-200 ${
                        activeTab === tabIndex
                          ? tabPillActive[tabIndex % tabPillActive.length]
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <Link
                href={viewAllHref}
                className="homepage-action-pill inline-flex shrink-0 items-center justify-center gap-1 self-start bg-white px-3.5 py-2 text-sm font-semibold text-amber-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-50 sm:self-auto"
              >
                Xem tất cả
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="10"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  aria-hidden
                >
                  <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </Link>
            </div>

            <div className="bg-[#f8f9fa] p-4 md:p-5">
              {products.length ? (
                <div className="flex items-stretch gap-1.5 sm:gap-2">
                  <button
                    type="button"
                    onClick={() => scrollSection(sectionIndex, -1)}
                    className="hidden h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200/80 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white hover:shadow-md md:flex"
                    aria-label="Xem sản phẩm trước"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                      />
                    </svg>
                  </button>
                  <div
                    ref={(el) => setScrollRef(sectionIndex, el)}
                    onDragStart={(event) => event.preventDefault()}
                    onPointerDown={(event) => handleTrackPointerDown(sectionIndex, event)}
                    onPointerMove={(event) => handleTrackPointerMove(sectionIndex, event)}
                    onPointerUp={(event) => finishTrackDrag(sectionIndex, event)}
                    onPointerCancel={(event) => finishTrackDrag(sectionIndex, event)}
                    onClickCapture={(event) => handleTrackClickCapture(sectionIndex, event)}
                    className={`no-scrollbar min-w-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-none px-1 py-1 [scroll-padding-inline:6px] [-webkit-overflow-scrolling:touch] [touch-action:pan-y] ${
                      draggingSection === sectionIndex
                        ? 'select-none snap-none'
                        : 'snap-none md:snap-x md:snap-proximity'
                    }`}
                  >
                    <div className="flex min-h-0 gap-3 pr-2 md:gap-4 md:pr-2">
                      {products.map((product, idx) => (
                        <FlashProductCard
                          key={product.id}
                          product={product}
                          idx={idx}
                          handleAddToCart={handleAddToCart}
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => scrollSection(sectionIndex, 1)}
                    className="hidden h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200/80 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white hover:shadow-md md:flex"
                    aria-label="Xem sản phẩm sau"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="rounded-none border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                  Chưa có sản phẩm phù hợp cho tab này.
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
